import { useTheme } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { loginFormSchema } from '@validationSchemas/login';
import { useMediaQueries } from '@utils/mediaQueries/mediaQueries';
import { MobileLogo } from './Login.styles';
import { Text, Flex } from '@components';
import AppLogo from '@assets/logos/swise_black_on_white.svg';

import { AccessAppRecordStatusEnum } from '@features/api/response/User';
import { deviceUtils } from '@utils/device/deviceUtils';
import LocalLoader from '@components/AppLoader/LocalLoader';
import { useLogin } from './Login.utils';
import { LoginForm } from './Components/LoginForm';
import { NotificationSent } from './Components/NotificationSent';
import { ScanQR } from './Components/ScanQR';
import { DownloadApp } from './Components/DownloadApp';
import { MessageContainer } from './Components/MessageContainer';
import { useRef } from 'react';
import { AccountDisabled } from './Components/AccountDisabled';

export const APPLE_STORE_APP_URL =
  'https://apps.apple.com/us/app/swise-access/id6738693688';
export const GOOGLE_PLAY_STORE_APP_URL =
  'https://play.google.com/store/apps/details?id=com.swise.SwiseAccess';

export const LoginPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isUserUsingMobile = deviceUtils.isUserUsingMobile();
  const { sm: isMobileResolution } = useMediaQueries();

  const timeoutId = useRef<NodeJS.Timeout | undefined>(undefined);

  const methods = useForm({
    resolver: yupResolver(loginFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      keepUserLoggedIn: false,
    },
  });

  const handleContinue = (isEmailVerified: boolean | undefined) => {
    if (isEmailVerified === false) {
      navigate('/email-verification');
    } else if (isEmailVerified === true) {
      navigate('/');
    }
  };

  const {
    handleLoginClick,
    onBackClick,
    resendNotification,
    setInfoPanelOpen,
    isLoading,
    qrImage,
    status,
  } = useLogin(isUserUsingMobile, handleContinue);

  const handleLogin = async () => {
    if (isUserUsingMobile) {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }

      const response = await resendNotification(
        methods.getValues(),
        isUserUsingMobile
      );
      timeoutId.current = await handleLoginClick(
        methods.getValues(),
        response.sessionId
      );
    } else {
      await handleLoginClick(methods.getValues());
    }
  };

  if (isLoading) {
    return <LocalLoader />;
  }

  if (
    (qrImage && qrImage !== '') ||
    status === AccessAppRecordStatusEnum.LOGIN_PENDING ||
    status === AccessAppRecordStatusEnum.READY ||
    status === AccessAppRecordStatusEnum.LOGIN_SUCCESSFUL ||
    status === AccessAppRecordStatusEnum.LOGIN_ERROR
  ) {
    return (
      <MessageContainer isMobile={isMobileResolution} onBackClick={onBackClick}>
        {isUserUsingMobile ? (
          <NotificationSent onResendNotification={handleLogin} />
        ) : (
          <ScanQR qrImage={qrImage} />
        )}
      </MessageContainer>
    );
  }

  if (
    status === AccessAppRecordStatusEnum.RECORD_NOT_FOUND ||
    status === AccessAppRecordStatusEnum.INITIALIZED
  ) {
    return (
      <MessageContainer isMobile={isMobileResolution} onBackClick={onBackClick}>
        <DownloadApp />
      </MessageContainer>
    );
  }

  if (status === AccessAppRecordStatusEnum.ACCOUNT_DISABLED) {
    return (
      <MessageContainer isMobile={isMobileResolution} onBackClick={onBackClick}>
        <AccountDisabled />
      </MessageContainer>
    );
  }

  return (
    <Flex
      height='100vh'
      flexDirection='column'
      gap={19}
      sx={{
        maxWidth: isMobileResolution ? '100%' : 750,
        width: '100%',
        flex: 1,
        alignItems: 'center',
        alignSelf: 'center',
      }}
    >
      {isMobileResolution ? (
        <Flex alignItems='center' flexDirection='column' pt={16}>
          <MobileLogo src={AppLogo} />
        </Flex>
      ) : null}
      <Flex
        flex={1}
        justifyContent={isMobileResolution ? 'flex-start' : 'center'}
        flexDirection='column'
        gap={16}
        width='100%'
      >
        <Flex flexDirection='column'>
          <Text variant='h4' fontWeight='fontWeightBold'>
            <FormattedMessage id='login.welcomeBack' />
          </Text>
          <Text
            variant='h5'
            fontWeight='fontWeightRegular'
            style={{
              color: theme.palette.grey.A100,
              marginTop: theme.spacing(3),
            }}
          >
            <FormattedMessage id='login.pleaseEnterDetails' />
          </Text>
          <FormProvider {...methods}>
            <LoginForm
              onSubmit={async (data) => {
                setInfoPanelOpen(true);
                handleLogin();
              }}
            />
          </FormProvider>
        </Flex>
        <Flex>
          <Text variant='h6' fontWeight='fontWeightRegular'>
            <FormattedMessage
              id='login.doNotHaveAccount'
              values={{
                signUp: (
                  <Text
                    data-testid='signUp-button'
                    fontWeight='fontWeightSemiBold'
                    variant='h6'
                    color='primary'
                    style={{ display: 'inline', cursor: 'pointer' }}
                    onClick={() => navigate('/signup')}
                  >
                    <FormattedMessage id='misc.signUp' />
                  </Text>
                ),
              }}
            />
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
};
